import React from 'react';
import { useHomeRedesignStrings } from './useHomeRedesignStrings';

/** Alternating card backgrounds (Figma-style cream / white). */
function cardTone(i: number): 'cream' | 'white' {
  return i % 2 === 1 ? 'cream' : 'white';
}

/** Varied slight tilts (not strict alternation) — index matches testimonial card order. */
const CARD_TILT_CLASSES = [
  'rotate-[1.65deg]',
  '-rotate-[1.95deg]',
  'rotate-[2.05deg]',
  '-rotate-[1.35deg]',
  'rotate-[1.8deg]',
] as const;

function cardTilt(i: number): string {
  return CARD_TILT_CLASSES[i % CARD_TILT_CLASSES.length];
}

const HomeRedesignTestimonials: React.FC = () => {
  const { testimonials: t } = useHomeRedesignStrings();

  return (
    <section className="py-10 lg:py-16">
      <div className="container-chisme">
        <h2 className="max-w-3xl font-instrument text-[2.25rem] leading-tight tracking-[-0.02em] text-[#2a0f2a] sm:text-5xl lg:text-[64px] lg:leading-[1] lg:tracking-[-0.02em]">
          <span className="block">{t.h2a}</span>
          <span className="block italic text-chisme-primary">{t.h2b}</span>
        </h2>
      </div>

      <div className="relative mt-8 w-full">
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-8 bg-gradient-to-r from-[#fcf7fa] to-transparent sm:w-12"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-8 bg-gradient-to-l from-[#fcf7fa] to-transparent sm:w-12"
          aria-hidden
        />

        <div
          className="scrollbar-hide mx-auto flex max-w-[1280px] snap-x snap-mandatory items-stretch gap-5 overflow-x-auto overflow-y-visible scroll-smooth px-6 py-4 sm:gap-6 sm:px-8 lg:gap-7 lg:px-12"
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-label={t.carouselAria}
        >
          {t.cards.map((card, i) => {
            const tone = cardTone(i);
            const tilt = cardTilt(i);
            return (
              <article
                key={`${card.name}-${i}`}
                className={`snap-center snap-always shrink-0 ${tilt} flex h-full w-[min(86vw,340px)] flex-col rounded-[28px] border border-[rgba(74,33,71,0.1)] p-7 shadow-[0px_1px_0px_rgba(74,33,71,0.04)] transition-transform duration-300 sm:w-[min(78vw,360px)] lg:w-[340px] ${
                  tone === 'cream' ? 'bg-[#faf1e8]' : 'bg-white'
                }`}
              >
                <p className="font-instrument text-xl leading-snug sm:text-2xl lg:text-[30px] lg:leading-[35.4px] lg:tracking-[-0.012em]">
                  <span className="italic text-chisme-primary">“ </span>
                  <span className="text-[#2a0f2a]">{card.body}</span>
                  <span className="italic text-chisme-primary"> ”</span>
                </p>
                <div className="mt-auto pt-6 font-jakarta">
                  <p className="text-[15px] font-bold text-[#2a0f2a]">{card.name}</p>
                  <p className="text-[13px] text-[#5a3158]">{card.place}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HomeRedesignTestimonials;
